import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import { FaUserPlus, FaShoppingCart } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BackButton from '../components/BackButton';
import { useHiringDeliverable } from '../hooks/useHiringDeliverable';
import PortalBackground from '../components/PortalBackground';

// overrideUserId / overrideUserEmail / overrideCompanyId let an internal viewer
// (CRM Portal View) load a specific client account's hiring instead of their own.
// singleColumn stacks the position cards one per row — used when the page is
// embedded in a narrow (e.g. half-width) container like the CRM Portal View.
const HRHiring = ({ embedded = false, singleColumn = false, overrideUserId = null, overrideUserEmail = null, overrideCompanyId = null }) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  // The uid/email whose positions + requests are read/written.
  const effectiveUid = overrideUserId || currentUser?.uid;
  const effectiveEmail = overrideUserEmail || currentUser?.email;
  const [showPositionRequestModal, setShowPositionRequestModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const hiringDeliverable = useHiringDeliverable(overrideCompanyId);
  const [positions, setPositions] = useState([]);
  const [positionRequest, setPositionRequest] = useState({
    position: '',
    requirements: '',
    urgency: 'normal',
    budget: ''
  });


  // Fetch positions data
  useEffect(() => {
    if (!currentUser) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let unsubscribe = null;
    
    const fetchPositions = async () => {
      try {
        // Use a simple query without orderBy to avoid index issues
        const positionsQuery = query(
          collection(db, 'positions'),
          where('userId', '==', effectiveUid)
        );
        
        unsubscribe = onSnapshot(positionsQuery, (snapshot) => {
          const positionsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort manually by createdAt
          positionsData.sort((a, b) => {
            const dateA = new Date(a.createdAt || 0);
            const dateB = new Date(b.createdAt || 0);
            return dateB - dateA;
          });
          setPositions(positionsData);
          setIsLoading(false);
        }, (error) => {
          console.error('Error fetching positions:', error);
          setPositions([]);
          setIsLoading(false);
        });
      } catch (error) {
        console.error('Error setting up positions query:', error);
        setPositions([]);
        setIsLoading(false);
      }
    };

    fetchPositions();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser, effectiveUid]);




  const handlePositionRequest = () => {
    setShowPositionRequestModal(true);
  };

  const handleSubmitPositionRequest = async () => {
    if (!currentUser) {
      alert('Please log in to submit a position request.');
      return;
    }

    // A request consumes one hiring service — block it if none are available.
    if (!hiringDeliverable.hasDeliverable || hiringDeliverable.remaining <= 0) {
      alert('You have no hiring services available. Please add one to request a position search.');
      setShowPositionRequestModal(false);
      navigate('/portal/billing#portal-plan');
      return;
    }

    try {
      setIsLoading(true);
      
      // Store position request in Firebase
      const requestData = {
        userId: effectiveUid,
        userEmail: effectiveEmail,
        position: positionRequest.position,
        requirements: positionRequest.requirements,
        urgency: positionRequest.urgency,
        budget: positionRequest.budget,
        status: 'in-progress',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const requestRef = await addDoc(collection(db, 'positionRequests'), requestData);
      
      // Create new position document
      const positionData = {
        userId: effectiveUid,
        title: positionRequest.position,
        department: 'TBD',
        location: 'TBD',
        type: 'Full-time',
        status: 'In Progress',
        applications: 0,
        postedDate: new Date().toISOString().split('T')[0],
        deadline: null,
        priority: positionRequest.urgency === 'critical' ? 'Critical' : 
                  positionRequest.urgency === 'high' ? 'High' : 
                  positionRequest.urgency === 'normal' ? 'Medium' : 'Low',
        description: positionRequest.requirements || 'Requirements to be defined',
        budget: positionRequest.budget,
        positionRequestId: requestRef.id, // Link to position request
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await addDoc(collection(db, 'positions'), positionData);

      // Count this request as one used hiring service so it shows as a completion
      // on the account's deliverables / Client Overview and the client's remaining
      // balance drops by one (flipping the card to "Add a Hiring Service" at zero).
      try {
        await hiringDeliverable.logCompletion({
          note: `Position search requested: ${positionRequest.position || 'Untitled position'}`,
        });
      } catch (deliverableError) {
        console.error('❌ Error recording hiring service usage:', deliverableError);
        // Don't fail the request if deliverable logging fails.
      }

      // Trigger HR fulfillment notification for position request
      try {
        let companyName = 'Unknown Company';
        try {
          const userDoc = await getDoc(doc(db, 'users', effectiveUid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            companyName = userData.company || userData.companyName || userData.businessName || companyName;
          }
        } catch (userError) {
          console.error('Error fetching user company for notification:', userError);
        }

        const { triggerFulfillmentNotification } = await import('../services/notificationTriggerService');
        await triggerFulfillmentNotification('hr', {
          type: 'position request',
          name: 'New Position Request',
          description: `Position request for: ${positionRequest.position}`,
          requestedBy: overrideUserEmail || currentUser.displayName || currentUser.email,
          company: companyName,
          position: positionRequest.position,
          requirements: positionRequest.requirements,
          urgency: positionRequest.urgency,
          budget: positionRequest.budget,
          timestamp: new Date().toISOString()
        });
        console.log('✅ HR fulfillment notification sent for position request');
      } catch (notificationError) {
        console.error('❌ Error sending HR fulfillment notification:', notificationError);
        // Don't fail the request if notification fails
      }
      
      // Reset form and close modal
      setPositionRequest({
        position: '',
        requirements: '',
        urgency: 'normal',
        budget: ''
      });
      setShowPositionRequestModal(false);
    } catch (error) {
      console.error('Error submitting position request:', error);
      alert('Error submitting position request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };




  const renderHirePlaceholderCard = () => {
    const { loading, hasDeliverable, remaining } = hiringDeliverable;
    const canStartHire = hasDeliverable && remaining > 0;

    // Loading: neutral, non-interactive placeholder while we resolve the plan.
    if (loading) {
      return (
        <div
          className={`p-6 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-center min-h-[9rem] ${
            darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'
          }`}
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-current mb-3" />
          <span className="text-sm">Checking your plan…</span>
        </div>
      );
    }

    // Has an available hiring deliverable → let them start a new hire.
    if (canStartHire) {
      return (
        <button
          type="button"
          onClick={handlePositionRequest}
          className={`p-6 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-center transition-colors min-h-[9rem] ${
            darkMode
              ? 'border-gray-600 text-gray-400 hover:border-red-500 hover:text-red-400'
              : 'border-gray-300 text-gray-500 hover:border-red-500 hover:text-red-600'
          }`}
        >
          <FaUserPlus className="h-8 w-8 mb-3" />
          <span className="text-lg font-semibold">Start a New Hire</span>
          <span className="mt-1 text-sm">
            {remaining} hiring {remaining === 1 ? 'service' : 'services'} available — request a position search
          </span>
        </button>
      );
    }

    // No hiring deliverable (or all used up) → prompt them to purchase one.
    return (
      <button
        type="button"
        onClick={() => navigate('/portal/billing#portal-plan')}
        className={`p-6 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-center transition-colors min-h-[9rem] ${
          darkMode
            ? 'border-gray-600 text-gray-400 hover:border-red-500 hover:text-red-400'
            : 'border-gray-300 text-gray-500 hover:border-red-500 hover:text-red-600'
        }`}
      >
        <FaShoppingCart className="h-8 w-8 mb-3" />
        <span className="text-lg font-semibold">Add a Hiring Service</span>
        <span className="mt-1 text-sm">
          {hasDeliverable
            ? "You've used all your hiring services — add one to start another hire"
            : 'Your plan has no hiring service yet — add one to get started'}
        </span>
      </button>
    );
  };

  const renderPositionsTab = () => {
    const activePositions = positions.filter(p => p.status === 'Requested' || p.status === 'Pending' || p.status === 'In Progress');
    const filledPositions = positions.filter(p => p.status === 'Completed');

    // In a narrow embed, stack cards one per row; otherwise use the full grid.
    const positionGridClass = singleColumn
      ? 'grid grid-cols-1 gap-6'
      : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';

    return (
      <div className="space-y-6">
        {/* Active Positions Section */}
        <div className="space-y-4">
          <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Active Positions ({activePositions.length})
          </h3>
          <div className={positionGridClass}>
            {activePositions.map((position) => (
              <div
                key={position.id}
                className={`p-6 rounded-lg shadow-md border cursor-pointer hover:shadow-lg transition-shadow ${
                  darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}
                onClick={() => navigate(`/portal/hr/hiring/position/${position.id}`)}
              >
                <div className="text-center">
                  <h3 className={`text-lg font-semibold mb-3 ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {position.title}
                  </h3>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    position.status === 'Requested'
                      ? darkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                      : position.status === 'Pending'
                      ? darkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
                      : position.status === 'In Progress'
                      ? darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                      : darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {position.status}
                  </span>
                </div>
              </div>
            ))}

            {/* Start-a-new-hire / purchase placeholder card, driven by the company's hiring deliverable */}
            {renderHirePlaceholderCard()}
          </div>
        </div>

        {/* Filled Positions Section - only shown once at least one position is filled */}
        {filledPositions.length > 0 && (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Filled Positions ({filledPositions.length})
            </h3>
            <div className={positionGridClass}>
              {filledPositions.map((position) => (
                <div
                  key={position.id}
                  className={`p-6 rounded-lg shadow-md border cursor-pointer hover:shadow-lg transition-shadow ${
                    darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                  onClick={() => navigate(`/portal/hr/hiring/position/${position.id}`)}
                >
                  <div className="text-center">
                    <h3 className={`text-lg font-semibold mb-3 ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {position.title}
                    </h3>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                    }`}>
                      {position.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={embedded ? '' : 'relative min-h-screen p-6'}>
      {!embedded && <PortalBackground />}
      <div className="relative max-w-7xl mx-auto">
        {/* Header - Outside container */}
        <div className="flex items-center justify-between mb-8 relative">
          <div className="flex-1 flex items-center">
            {!embedded && <BackButton onClick={() => navigate('/portal/hr')} />}
          </div>
          {!embedded && (
            <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
              <FaUserPlus className={`text-4xl mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
              <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Hiring</h1>
            </div>
          )}
          <div className="flex-1 flex justify-end" />
        </div>

        {/* Main Container - Only positions */}
        <div>
          <div className="space-y-6">
            {/* Content */}
            <div className="mt-6">
              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <BBALoader size={48} />
                </div>
              ) : (
                renderPositionsTab()
              )}
            </div>


      {/* Position Request Modal */}
      {showPositionRequestModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-lg`}>
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => setShowPositionRequestModal(false)}
                className={`text-gray-400 hover:text-gray-600 ${darkMode ? 'hover:text-gray-300' : 'hover:text-gray-600'}`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h2 className={`text-xl font-bold text-center flex-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Request Position Search
              </h2>
              <div className="w-6"></div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Position Title *
                </label>
                                 <input
                   type="text"
                   value={positionRequest.position}
                   onChange={(e) => setPositionRequest({...positionRequest, position: e.target.value})}
                   placeholder="Project Manager"
                   className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                     darkMode 
                       ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                       : 'border-gray-300 text-gray-900 placeholder-gray-500'
                   }`}
                 />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Requirements & Notes
                </label>
                <textarea
                  value={positionRequest.requirements}
                  onChange={(e) => setPositionRequest({...positionRequest, requirements: e.target.value})}
                  placeholder="Describe the role requirements, experience needed, or any specific notes for our HR expert..."
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Budget
                </label>
                <input
                  type="text"
                  value={positionRequest.budget}
                  onChange={(e) => setPositionRequest({...positionRequest, budget: e.target.value})}
                  placeholder="e.g., $80,000 - $120,000"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowPositionRequestModal(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  darkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitPositionRequest}
                disabled={!positionRequest.position.trim()}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  !positionRequest.position.trim()
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRHiring; 